import React, {
  cloneElement,
  forwardRef,
  isValidElement,
  ReactElement,
  useEffect,
  useRef,
  useState,
} from "react";
import classNames from "classnames";
import uniqueId from "lodash.uniqueid";
import { get } from "../utils";
import { TooltipProps } from "./tooltip.types";
import {
  arrow,
  autoUpdate,
  flip,
  FloatingPortal,
  offset,
  Placement,
  shift,
  useDismiss,
  useFloating,
  useFocus,
  useHover,
  useInteractions,
  useMergeRefs,
  useRole,
} from "@floating-ui/react";
import { ConditionalWrapper } from "../utils/ConditionalWrapper";
import { useCombinedPopoverRefs } from "../popover/useCombinedPopoverRefs";
import { getArrowCoords } from "../utils/dom/floating-ui-utils/getArrowCoords";
import { matchPositionKey } from "../utils/dom/floating-ui-utils/matchPositionKey";
import { matchCaretKey } from "../utils/dom/floating-ui-utils/matchCaretKey";

export const Tooltip = forwardRef(
  (
    {
      id,
      className,
      anchorX = "center",
      anchorY = "top",
      toggleEl,
      children,
      portalWrapperClassName,
      toggleElWrapperClassName,
      toggleElWrapperRef,
      toggleElWrapperStyle,
      ...rest
    }: TooltipProps,
    containerExternalRef
  ): ReactElement => {
    const { current: _uniqueTooltipId } = useRef(uniqueId("spark-tooltip_"));
    const uniqueTooltipId = id || _uniqueTooltipId;
    const arrowRef = useRef(null);
    const [placementProp, setPlacementProp] = useState<Placement | undefined>();
    type Arrow = "below" | "above" | "right" | "left";
    const [arrowDirection, setArrowDirection] = useState<Arrow | undefined>();

    const [isOpen, setIsOpen] = React.useState(false);
    let isAvoidWrapper = false;
    let isBadge = false;
    if (isValidElement(toggleEl)) {
      const toggleElType: any = toggleEl?.type;
      isAvoidWrapper = toggleEl.type === "button" || toggleEl.type === "span";
      isBadge = toggleElType.displayName === "Badge";
    }
    const cloneClasses = isAvoidWrapper
      ? get(toggleEl, "props.className") ?? ""
      : classNames("spark-inline-block", toggleElWrapperClassName);

    const { refs, floatingStyles, context, middlewareData, placement } =
      useFloating({
        open: isOpen,
        onOpenChange: setIsOpen,
        // use pre-defined positions
        placement: placementProp,
        // middleware are options to position (and customize) the popover
        middleware: [
          offset({
            mainAxis: 8,
            // crossAxis: 130, // for custom use cases when pre-define postions will not solve problem
          }),
          // popover flips to any placement around the target element
          flip({
            fallbackAxisSideDirection: "end",
            // fallbackPlacements: !autoFlip ? [] : undefined,
          }),
          // popover shifts only along an axis
          shift({ mainAxis: true, crossAxis: false }),
          arrow({
            element: arrowRef,
          }),
        ],
        transform: false,
        whileElementsMounted: autoUpdate,
      });

    useCombinedPopoverRefs(refs.floating, containerExternalRef);
    const toggleElRef = useMergeRefs([
      refs.setReference,
      isAvoidWrapper ? (toggleEl as any)?.ref : toggleElWrapperRef,
    ]);

    useEffect(() => {
      setPlacementProp(matchPositionKey(`${anchorX}-${anchorY}`));
      setArrowDirection(matchCaretKey(placement));
    }, [placement, anchorX, anchorY]);

    // Event listeners to change the open state
    const hover = useHover(context, { move: false });
    const focus = useFocus(context);
    const dismiss = useDismiss(context);
    // Role props for screen readers
    const role = useRole(context, { role: "tooltip" });

    // Merge all the interactions into prop getters
    const { getReferenceProps, getFloatingProps } = useInteractions([
      hover,
      focus,
      dismiss,
      role,
    ]);

    const additionalProps = isBadge
      ? { _privateProps: { hasTooltip: true } }
      : {};

    const modifiedToggleEl = isAvoidWrapper ? (
      toggleEl
    ) : (
      <div style={toggleElWrapperStyle}>
        {isBadge
          ? cloneElement(toggleEl as ReactElement, {
              ...additionalProps,
              className: classNames(
                "spark-tooltip tooltip-initialized",
                get(toggleEl, "props.className")
              ),
            })
          : toggleEl}
      </div>
    );

    const renderTooltipContent = (): ReactElement => {
      return (
        <ConditionalWrapper
          condition={!!portalWrapperClassName}
          wrapper={(children): ReactElement => (
            <div className={portalWrapperClassName}>{children}</div>
          )}
        >
          <div
            id={uniqueTooltipId}
            role="tooltip"
            className={classNames(
              "spark-tooltip__content",
              { active: isOpen },
              className
            )}
            ref={refs.setFloating}
            {...getFloatingProps()}
            {...rest}
            style={{ ...floatingStyles, ...rest.style }}
          >
            {children}
            <span
              ref={arrowRef}
              id="arrow"
              className="spark-tooltip__caret"
              data-position={arrowDirection}
              style={getArrowCoords(middlewareData, arrowDirection)}
            ></span>
          </div>
        </ConditionalWrapper>
      );
    };

    const toggleElProps = isAvoidWrapper
      ? (toggleEl as ReactElement).props
      : {};

    return (
      <>
        {cloneElement(modifiedToggleEl as ReactElement, {
          ...getReferenceProps({
            ref: toggleElRef,
            ...toggleElProps,
            className: classNames(
              { "spark-tooltip": !isBadge },
              { "tooltip-initialized": !isBadge },
              { "tooltip-active": isOpen },
              cloneClasses
            ),
          }),
        })}
        {isOpen && <FloatingPortal>{renderTooltipContent()}</FloatingPortal>}
      </>
    );
  }
);

Tooltip.displayName = "Tooltip";
